import type { ExternalToast } from "vue-sonner";
import { toast } from "vue-sonner";

export const TOAST_DURATION_MS = {
  success: 4_000,
  info: 4_000,
  warning: 6_000,
  error: Number.POSITIVE_INFINITY,
} as const;

type TimedToastType = keyof typeof TOAST_DURATION_MS;

let installed = false;

/**
 * vue-sonner has one duration for the whole Toaster, so the per type durations are set here: every
 * toast.success/info/warning/error() call without its own `duration` gets the one of its type. The exception is a
 * call that updates a toast with that `id` which is still showing: it keeps the duration that toast already has,
 * because sonner starts its close timer once and ignores a new duration on update.
 *
 * Runs when this module is imported, and the app entries import it, so a toast fired before the Toaster
 * mounts gets its duration too.
 */
export function applyToastDurations(): void {
  if (installed) {
    return;
  }
  installed = true;

  for (const type of Object.keys(TOAST_DURATION_MS) as TimedToastType[]) {
    const original = toast[type];
    toast[type] = (message: Parameters<typeof original>[0], data?: ExternalToast) => {
      const isUpdate = data?.id !== undefined && toast.getToasts().some(showing => showing.id === data.id);
      const hasOwnTiming = data?.duration !== undefined || isUpdate;
      return original(message, hasOwnTiming ? data : { ...data, duration: TOAST_DURATION_MS[type] });
    };
  }
}

applyToastDurations();
